import { cva, type VariantProps } from 'class-variance-authority'
import { motion } from 'framer-motion'
import type { LucideIcon } from 'lucide-react'
import { X } from 'lucide-react'

import { cn } from 'src/shared/utils/cn'

const badgeVariants = cva('inline-flex items-center whitespace-nowrap rounded-full border transition-colors', {
  variants: {
    variant: {
      default: 'border-transparent bg-primary text-primary-foreground',
      secondary: 'border-secondary/60 bg-secondary/5 text-foreground',
      accent: 'border bg-accent',
      destructive: 'border-destructive/80 bg-destructive/20 text-destructive',
      outline: 'text-foreground',
      warning: 'border-[#FADEAD] bg-[#FCECCF] text-primary',
    },
    size: {
      default: 'space-x-2 px-3.5 py-2 font-medium',
      small: 'space-x-2 px-2.5 py-1.5 text-[13px] font-medium',
      mini: 'space-x-1.5 px-2 py-1 text-[12px] font-medium',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'default',
  },
})

type BadgeBaseProps = {
  icon?: LucideIcon
  text: string
  showCloseIcon?: boolean
  iconColor?: string
  iconClassName?: string
  rightIcon?: LucideIcon
  variant?: VariantProps<typeof badgeVariants>['variant']
  size?: VariantProps<typeof badgeVariants>['size']
  className?: string
}

type BadgeAsButtonProps = BadgeBaseProps & {
  onClick: () => void
  showCloseIcon?: boolean
}

type BadgeAsDivProps = BadgeBaseProps & {
  onClick?: never
  showCloseIcon?: never
}

export type BadgeProps = BadgeAsButtonProps | BadgeAsDivProps

function Badge({
  className,
  variant,
  size,
  icon: Icon,
  text,
  showCloseIcon,
  onClick,
  iconColor,
  iconClassName,
  rightIcon: RightIcon,
}: BadgeProps) {
  const content = (
    <>
      {Icon && (
        <Icon
          className={cn(
            `${size === 'mini' ? 'size-3' : size === 'small' ? 'size-3.5' : 'size-5'}`,
            iconColor,
            iconClassName,
          )}
        />
      )}
      <span>{text}</span>
      {showCloseIcon && (
        <span
          className={cn(
            'flex shrink-0 grow-0 cursor-pointer items-center justify-center rounded-full bg-primary text-white',
            size === 'mini' ? 'size-2' : size === 'small' ? 'size-2.5' : 'size-4',
          )}
        >
          <X className={cn(size === 'mini' ? 'size-1' : size === 'small' ? 'size-1.5' : 'size-2.5')} />
        </span>
      )}
      {RightIcon && (
        <RightIcon
          className={cn(`${size === 'mini' ? 'size-2' : size === 'small' ? 'size-3.5' : 'size-5'}`, iconColor)}
        />
      )}
    </>
  )

  if (onClick || showCloseIcon) {
    return (
      <motion.button
        type="button"
        onClick={onClick}
        className={cn(badgeVariants({ variant, size }), className, 'cursor-pointer')}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        style={{ transformOrigin: 'center' }}
      >
        {content}
      </motion.button>
    )
  }

  return <div className={cn(badgeVariants({ variant, size }), className)}>{content}</div>
}

export { Badge, badgeVariants }
